<template>
  <div
    v-if="visible"
    class="magic-ui-textarea"
    :class="{
      labelTop: config.labelStyle.location == 'top',
      labelLeft: config.labelStyle.location == 'left',
    }"
  >
    <div
      class="input-label"
      v-if="config.label.text"
      :style="{
        '--label-margin': config.labelStyle.margin,
        '--label-color': config.labelStyle.color,
        '--label-font-size': config.labelStyle.fontSize,
        '--label-font-weight': config.labelStyle.fontWeight,
        '--label-width': config.labelStyle.width,
        '--label-align': config.labelStyle.align,
      }"
    >
      <span v-if="config.verification.required" style="color: red">*</span
      ><span>{{ config.label.text }} </span>
    </div>
    <div ref="inputRef" class="input-wrap" :style="{
      '--input-focus-border-color': config.inputStyle.borderFocusColor
    }">
      <textarea
        class="magic-ui-textarea"
        :maxlength="config.verification.maxlength"
        v-model="valueRef"
        :style="inputStyle"
        :name="config.id"
        :rows="config.rows * 1"
        :autoComplete="config.autoComplete"
        :placeholder="config.placeholder"
        :type="config.inputType"
        :disabled="config.disabled"
        :readOnly="config.readOnly"
        :defaultValue="config.value"
        @focus="handleFocus"
        @blur="handleBlur"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, watch, ref, reactive, watchEffect, inject } from 'vue';

import Core from '@tmagic/core';

import useApp from '@/hooks/useApp';
import useDisplay from '@/hooks/useDisplay';
import initValue from '../initValue';
import useErrorTips from '../../../useErrorTips';

const props = defineProps<{
  config: typeof initValue;
}>();

const app: Core | undefined = useApp(props);
const node = app?.page?.getNode(props.config.id);
const valueRef = ref(props.config.value);
const inputRef = ref();
const errorTips = ref('');
useErrorTips(errorTips, inputRef, props.config);

const inputStyle = computed(() => app?.transformStyle(props.config.inputStyle));
watch(
  () => props.config.value,
  (val) => {
    if (window.location.pathname.indexOf('playground') >= 0) {
      valueRef.value = val;
    }
  }
);
const style = computed(() => {
  return app?.transformStyle(props.config.style || {});
});
const handleFocus = () => {
  console.log(`组件${props.config.id}的触发了textarea:focus事件并广播`);
  app?.emit('textarea:focus', node);
};

const handleChange = () => {
  console.log(`组件${props.config.id}的触发了textarea:change事件并广播`);

  app?.emit('textarea:change', node);
};
watch(
  () => valueRef.value,
  (val) => {
    handleChange();
  }
);

const setValue = (val: string) => {
  valueRef.value = val;
};
const clearValue = () => {
  valueRef.value = '';
};
const focus = () => {
  inputRef.value.focus();
};

const blur = () => {
  inputRef.value.blur();
};

const rules = reactive<any[]>([]);
watchEffect(() => {
  rules.splice(0, rules.length);
  const { required, minlength, type, regexp, customRule } =
    props.config.verification;
  if (required) {
    rules.push({
      validator(rule: any, value: any, callback: any) {
        if (
          valueRef.value === undefined ||
          valueRef.value === null ||
          valueRef.value === ''
        ) {
          return '必填项';
        }
      },
    });
  }
  if (minlength && Number(minlength) > 0) {
    rules.push({
      validator(rule: any, value: any, callback: any) {
        if (valueRef.value && valueRef.value.length < Number(minlength)) {
          return `最少输入${minlength}个字符`;
        }
      },
    });
  }
  if (type === 'number') {
    rules.push({
      validator(rule: any, value: any, callback: any) {
        if (valueRef.value && isNaN(Number(valueRef.value))) {
          return '请输入数字';
        }
      },
    });
  }
  if (type === 'email') {
    rules.push({
      validator(rule: any, value: any, callback: any) {
        if (
          valueRef.value &&
          !/^[a-zA-Z0-9_-]+@[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)+$/.test(
            valueRef.value
          )
        ) {
          return '请输入正确的邮箱';
        }
      },
    });
  }
  if (type === 'url') {
    rules.push({
      validator(rule: any, value: any, callback: any) {
        if (
          valueRef.value &&
          !/^(https?|ftp|file):\/\/.+/.test(valueRef.value)
        ) {
          return '请输入正确的网址';
        }
      },
    });
  }
  if (type === 'regexp' && regexp) {
    rules.push({
      validator() {
        if (valueRef.value && !new RegExp(regexp).test(valueRef.value)) {
          return '请输入正确的格式';
        }
      },
    });
  }
  if (customRule && typeof customRule === 'function') {
    rules.push({
      validator: () => {
        const result = customRule(valueRef.value);
        return result;
      },
    });
  }
});
const validate = async () => {
  for (const rule of rules) {
    const tips = await rule.validator();
    if (tips) {
      errorTips.value = tips;
      return false;
    }
  }
  errorTips.value = '';
  return true;
};
const clearValidate = () => {
  errorTips.value = '';
};

const useFormField = inject<any>('useFormField', undefined);
if (useFormField) {
  useFormField({
    fieldPropsConfig: props.config,
    fieldValueRef: valueRef,
    validate,
    clearValidate,
  });
}
const handleBlur = () => {
  validate();
  console.log(`组件${props.config.id}的触发了textarea:blur事件并广播`);
  app?.emit('textarea:blur', node);
};
const { visible, show, hide } = useDisplay(props);
// 暴露给编辑器的接口请注册在这里
const exportInterface = reactive({
  errorMessage: errorTips,  
  value: valueRef,
  setValue,
  clearValue,
  show,
  hide,
  focus,
  blur,
});

defineExpose(exportInterface);
</script>
<style lang="scss" scoped>
.magic-ui-textarea {
  display: flex;
  flex: 1;
  box-sizing: border-box;
  border: 0;
  width: 100%;
  .input-label {
    flex-shrink: 0;
    font-size: var(--label-font-size);
    color: var(--label-color);
    font-weight: var(--label-font-weight);
    min-width: var(--label-width);
    justify-content: var(--label-align);
    display: flex;
  }
  &.labelTop {
    display: inline-block;
    .input-label {
      margin-bottom: var(--label-margin);
    }
  }
  &.labelLeft {
    display: flex;
    align-items: center;
    .input-label {
      margin-right: var(--label-margin);
    }
  }
  textarea {
        &:focus {
      border-color: var(--input-focus-border-color) !important;
    }
  }
  .input-wrap {
    flex: 1;
  }
  ::-webkit-input-placeholder {
    color: #bbb;
  }
  :-ms-input-placeholder {
    color: #bbb;
  }
  ::placeholder {
    color: #bbb;
  }
}
.error-tips {
  color: red;
  margin-top: 2px;
  font-size: 12px;
}
</style>
